import type { ScheduleAssignmentRow } from '@routeback/core';
import { Empty } from '@routeback/ui';
import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useApp } from '../../lib/app';
import { formatRange, holidays, todayIn, addDays } from '../timeoff/timeoff';
import {
  DAY_SHORT, assignmentFor, availableByDay, dayInfo, inTerminal, myWeek, shortName, sortRoutes, weekDates, weekStartDay, weekStartOf,
} from './schedule';
import { useLineup, useRoster, useWeekData } from './useScheduleData';

const md = (d: string) => new Date(d + 'T12:00:00Z').toLocaleDateString('en-US', { timeZone: 'UTC', month: 'short', day: 'numeric' });
const dow = (d: string) => DAY_SHORT[new Date(d + 'T12:00:00Z').getUTCDay()]!;

/**
 * Driver: my week (route, notes), the team's week (routes × days, like
 * the Driver Portal), who's free each day, and a link to the truck lineup.
 * Read-only; managers build it in Phase 4. Works offline.
 */
export function ScheduleView() {
  const app = useApp();
  const today = todayIn(app.details.timezone);
  const startDow = weekStartDay(app.settings);
  const [start, setStart] = useState(() => weekStartOf(today, startDow));
  const [picked, setPicked] = useState<{ routeId: string; date: string } | null>(null);
  const dates = useMemo(() => weekDates(start), [start]);
  const from = dates[0]!;
  const to = dates[6]!;
  const { routes, assignments, days } = useWeekData(from, to);
  const { roster, loaded } = useRoster(from, to);
  const lineup = useLineup();

  const names = new Map(roster.map((r) => [r.user_id, r.display_name]));
  if (!names.has(app.userId)) names.set(app.userId, app.userName || 'You');
  const routeById = new Map(routes.map((r) => [r.id, r]));
  const termName = new Map(app.terminals.map((t) => [t.id, t.name]));
  const hol = { ...holidays(+from.slice(0, 4)), ...holidays(+to.slice(0, 4)) };
  const shownRoutes = sortRoutes(routes.filter((r) => inTerminal(r.terminal_id, app.terminal)));
  const myOff = roster.find((r) => r.user_id === app.userId)?.off_dates ?? [];
  const week = myWeek(dates, assignments, app.userId, myOff);
  const free = availableByDay(dates, roster, assignments);
  const isThisWeek = from === weekStartOf(today, startDow);

  const hasLineup = lineup.sections.length > 0;

  const describe = (a: ScheduleAssignmentRow) => {
    const r = a.route_id ? routeById.get(a.route_id) : undefined;
    const bits = [r ? `Route ${r.code}${r.name ? ` (${r.name})` : ''}` : 'Route'];
    const term = a.terminal_id ?? r?.terminal_id;
    if (app.multiTerminal && term) bits.push(termName.get(term) ?? '');
    return bits.filter(Boolean).join(' · ');
  };

  const step = (n: number) => { setPicked(null); setStart((s) => addDays(s, 7 * n)); };
  const pickedRoute = picked ? routeById.get(picked.routeId) : undefined;
  const pickedA = picked ? assignmentFor(assignments, picked.routeId, picked.date) : undefined;
  const pickedInfo = picked ? dayInfo(days, picked.date, picked.routeId) : null;

  return (
    <div>
      <div className="card row" style={{ justifyContent: 'space-between', gap: 6 }}>
        <button className="btn btn-sm btn-ghost" aria-label="Previous week" onClick={() => step(-1)}>‹</button>
        <div style={{ textAlign: 'center' }}>
          <strong>{formatRange(from, to)}</strong>
          {!isThisWeek && <div><button className="btn btn-sm btn-ghost" onClick={() => { setPicked(null); setStart(weekStartOf(today, startDow)); }}>This week</button></div>}
        </div>
        <button className="btn btn-sm btn-ghost" aria-label="Next week" onClick={() => step(1)}>›</button>
      </div>

      <div className="section-title">My week</div>
      <div className="list">
        {week.map((d) => {
          const h = hol[d.date];
          return (
            <div key={d.date} className={'list-item' + (d.date === today ? ' sched-today' : '')}>
              <div style={{ width: 56, flexShrink: 0 }}>
                <div style={{ fontWeight: 600 }}>{dow(d.date)}</div>
                <div className="muted small">{md(d.date)}</div>
              </div>
              <div className="grow">
                {d.kind === 'work' && d.assignments.map((a) => {
                  const info = dayInfo(days, d.date, a.route_id);
                  return (
                    <div key={a.id}>
                      <div>{describe(a)}{!info.open && <span className="chip" style={{ marginLeft: 6 }}>Route off</span>}</div>
                      {[a.note, ...info.notes].filter(Boolean).map((n, i) => <div key={i} className="muted small">📝 {n}</div>)}
                    </div>
                  );
                })}
                {d.kind === 'timeoff' && <div>🏖 Off</div>}
                {d.kind === 'none' && <div className="muted">Not scheduled</div>}
                {h && <div className="small" style={{ color: 'var(--gold)' }}>🎉 {h}</div>}
              </div>
            </div>
          );
        })}
      </div>

      {hasLineup && (
        <Link to="/lineup" className="card row settings-link" style={{ justifyContent: 'space-between' }}>
          <div className="grow">
            <strong>🅿️ Truck lineup</strong>
            <div className="muted small">
              Where every truck is parked
            </div>
          </div>
          <span aria-hidden>›</span>
        </Link>
      )}

      <div className="section-title">Team schedule</div>
      {shownRoutes.length === 0 ? (
        <div className="card"><Empty>No routes yet — your manager sets up routes and the schedule.</Empty></div>
      ) : (
        <div className="card" style={{ padding: 8 }}>
          <div className="sched-scroll">
            <div className="sched-grid" role="grid" aria-label="Team schedule">
              <div className="sched-head sched-route">Route</div>
              {dates.map((d) => (
                <div key={d} className={'sched-head' + (d === today ? ' sched-head-today' : '')}>{dow(d)}<br /><span className="muted">{md(d).split(' ')[1]}</span></div>
              ))}
              {shownRoutes.map((r) => (
                <div key={r.id} className="sched-row" role="row">
                  <div className="sched-route" title={r.name}>{r.code}</div>
                  {dates.map((d) => {
                    const info = dayInfo(days, d, r.id);
                    const a = assignmentFor(assignments, r.id, d);
                    const mine = a?.user_id === app.userId;
                    const cls = ['sched-cell'];
                    let label: string;
                    if (!info.open) { cls.push('sched-off'); label = 'Off'; }
                    else if (a?.user_id) { label = shortName(names.get(a.user_id) ?? 'Driver'); if (mine) cls.push('sched-mine'); }
                    else { cls.push('sched-open'); label = 'Open'; }
                    if (picked?.routeId === r.id && picked.date === d) cls.push('sched-picked');
                    return (
                      <button key={d} type="button" className={cls.join(' ')} onClick={() => setPicked(picked?.routeId === r.id && picked.date === d ? null : { routeId: r.id, date: d })}
                        aria-label={`Route ${r.code}, ${dow(d)} ${md(d)}: ${label}`}>
                        {label}{(info.notes.length > 0 || !!a?.note) && <span className="sched-dot" aria-hidden />}
                      </button>
                    );
                  })}
                </div>
              ))}
            </div>
          </div>
          {picked && pickedRoute && pickedInfo && (
            <div className="cal-day-panel">
              <strong>Route {pickedRoute.code} · {dow(picked.date)} {md(picked.date)}</strong>
              <div className="small" style={{ marginTop: 4 }}>
                {!pickedInfo.open ? 'Route is off this day' : pickedA?.user_id ? (names.get(pickedA.user_id) ?? 'Driver') + (pickedA.user_id === app.userId ? ' (you)' : '') : 'Open — nobody assigned yet'}
              </div>
              {[pickedA?.note ?? '', ...pickedInfo.notes].filter(Boolean).map((n, i) => <div key={i} className="small muted" style={{ marginTop: 2 }}>📝 {n}</div>)}
            </div>
          )}
          {app.mode === 'cloud' && !loaded && <p className="muted small">Loading names…</p>}
        </div>
      )}

      {shownRoutes.length > 0 && roster.length > 1 && (
        <details className="card">
          <summary><strong>Available drivers by day</strong> <span className="muted small">— not scheduled and not off</span></summary>
          <p className="muted small">Want a swap or an extra day? Talk to your manager.</p>
          {dates.map((d) => (
            <div key={d} className="row" style={{ alignItems: 'baseline', gap: 8, marginTop: 6 }}>
              <span style={{ width: 70, flexShrink: 0 }} className="small"><strong>{dow(d)}</strong> {md(d)}</span>
              <span className="small">{free[d]!.length ? free[d]!.map((r) => r.display_name).join(', ') : <span className="muted">None</span>}</span>
            </div>
          ))}
        </details>
      )}
    </div>
  );
}
